import WorkingCapitalBreakdown from "../components/workcap/WorkingCapitalBreakdown";
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { X, Filter } from "lucide-react";
import EndingInventoryCard from "../components/valuation/EndingInventoryCard";
import { OriginScope, OriginSummary, OriginBadge, syn } from "../lib/origin.jsx";
import { useInventoryValuation } from "../lib/valuation";
import { useRole } from "../lib/role.jsx";
import { BRANCHES, SKUS, CATEGORIES } from "../lib/data";
import {
  DOH_HISTOGRAM, STOCKOUT_CALENDAR, DEAD_STOCK, REPLEN_QUEUE, TURNOVER_TREND,
} from "../lib/dataExtras";
import { Tile, Badge, SectionHeader, StatusDot } from "../components/Primitives";
import InventoryDrillDrawer from "../components/InventoryDrillDrawer";
import InventoryEnginesPanel from "../components/InventoryEnginesPanel";
import AbcXyzMatrix from "../components/classification/AbcXyzMatrix";
import CellDrillDrawer from "../components/classification/CellDrillDrawer";
import PolicyRulesPanel from "../components/classification/PolicyRulesPanel";
import { getSummary } from "../lib/classificationApi";
import { getInventoryDrill } from "../lib/dataInventoryDetail";
import { formatPeso, formatNumber, pct, cn } from "../lib/utils";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, LineChart, Line, ReferenceArea, Legend,
} from "recharts";
import { useChartColors } from "../lib/chartColors";
import { CheckCircle2, XCircle, Clock, AlertTriangle, Trash2, ArrowRightLeft, ChevronRight } from "lucide-react";

const tickStyle = { fill: "rgb(var(--muted))", fontSize: 10, fontFamily: "IBM Plex Mono" };

function TT({ active, payload, label, formatter }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface border border-border-strong px-3 py-2 text-xs rounded-sm shadow-lg">
      <div className="text-[10px] uppercase tracking-widest text-muted mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 font-mono-tabular">
          <span className="w-2 h-2" style={{ background: p.color || p.payload?.fill }} />
          <span className="text-muted">{p.name}</span>
          <span className="ml-auto font-semibold">{formatter ? formatter(p.value) : p.value}</span>
        </div>
      ))}
    </div>
  );
}

function buildHeatmap() {
  const branches = BRANCHES.slice(0, 18);
  const skus = SKUS.slice(0, 16);
  const matrix = skus.map(sku => ({
    sku,
    cells: branches.map(b => {
      const seed = (sku.id.charCodeAt(8) * 13 + b.id.charCodeAt(5) * 7) % 100;
      const v = (seed / 100) ** 1.3 * 110 - 5;
      let state;
      if (v < 0) state = "stockout";
      else if (v < 15) state = "low";
      else if (v < 45) state = "healthy";
      else if (v < 75) state = "high";
      else state = "overstock";
      return { branch: b, value: Math.max(0, Math.round(v)), state };
    }),
  }));
  return { branches, matrix };
}

const HEAT_COLORS = {
  stockout: { bg: "rgb(var(--critical) / 0.85)", txt: "var(--primary-fg)" },
  low: { bg: "rgb(var(--critical) / 0.35)", txt: "var(--fg)" },
  healthy: { bg: "rgb(var(--success) / 0.45)", txt: "var(--fg)" },
  high: { bg: "rgb(var(--warning) / 0.45)", txt: "var(--fg)" },
  overstock: { bg: "rgb(var(--warning) / 0.85)", txt: "var(--fg)" },
};

const CAL_COLORS = ["rgb(var(--success) / 0.25)", "rgb(var(--warning) / 0.55)", "rgb(var(--critical) / 0.7)", "rgb(var(--critical))"];

export default function InventoryIntelligence() {
  return (
    <OriginScope>
      <InventoryIntelligenceBody />
    </OriginScope>
  );
}

function BranchFilterChip({ branch, locked, onClear }) {
  return (
    <div data-testid="inventory-branch-filter"
         className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/50 bg-primary/10 text-primary text-[11.5px] font-medium">
      <Filter size={11} /> {branch.name} · {branch.region}
      <span className="text-[10px] text-muted font-normal">valuation, ABC × XYZ classes, replenishment queue &amp; dead stock filtered</span>
      {!locked && (
        <button data-testid="inventory-branch-filter-clear" onClick={onClear} aria-label="Clear branch filter"
                className="w-5 h-5 rounded-full hover:bg-primary/20 flex items-center justify-center"><X size={11} /></button>
      )}
    </div>
  );
}

function InventoryIntelligenceBody() {
  const C = useChartColors();
  const { branchScope } = useRole();
  const [params, setParams] = useSearchParams();
  const branchId = branchScope?.branchId || params.get("branch") || null;
  const branch = branchId ? BRANCHES.find(b => b.id === branchId) : null;
  const valuation = useInventoryValuation(branch ? branch.id : null);
  const replenRows = branch ? REPLEN_QUEUE.filter(r => r.branchId === branch.id) : REPLEN_QUEUE;
  const deadRows = branch ? DEAD_STOCK.filter(d => d.branchId === branch.id) : DEAD_STOCK;
  const { branches, matrix } = useMemo(buildHeatmap, []);
  const [selectedCell, setSelectedCell] = useState(null);
  const [replenStatus, setReplenStatus] = useState({}); // {id: "approved" | "held"}

  // Drill-drawer state — one component drives 5 hotspot types
  const [drill, setDrill] = useState(null); // { type, payload }
  const drillData = drill ? getInventoryDrill(drill.type, drill.payload) : null;
  const openDrill = (type, payload) => setDrill({ type, payload });
  const closeDrill = () => setDrill(null);

  // ABC × XYZ pipeline summary — the network's rules, applied to the selected branch's own sales when one is chosen
  const [summary, setSummary] = useState(null);
  const [summaryError, setSummaryError] = useState(null);
  const [cellDrill, setCellDrill] = useState(null);
  const scopeId = branch?.id || null;
  useEffect(() => {
    let alive = true;
    setSummary(null); setSummaryError(null); setCellDrill(null);
    getSummary(scopeId).then(s => alive && setSummary(s)).catch(e => alive && setSummaryError(e.message));
    return () => { alive = false; };
  }, [scopeId]);

  return (
    <div className="space-y-6 animate-fade-in">
      <SectionHeader eyebrow="PART V · AI Layer" title="Inventory Intelligence"
        description="Ending inventory value, network-wide stockout risk, ABC×XYZ classification, DOH distribution, dead-stock recovery and AI replenishment queue."
        action={branch && <BranchFilterChip branch={branch} locked={!!branchScope} onClear={() => setParams({})} />} />

      <OriginSummary />

      <EndingInventoryCard valuation={valuation} branchName={branch?.name} />

      {/* Seven-engine AI recommendations — real backend-scored actions */}
      <WorkingCapitalBreakdown />
      <InventoryEnginesPanel />

      {/* KPI strip — each cell click-drills to a detail drawer */}
      <div className="tile grid grid-cols-2 md:grid-cols-6 divide-x divide-border">
        <Agg testid="kpi-active-skus" origin={syn("SKU master")} label="Active SKUs" value={formatNumber(SKUS.length * 60)} onClick={() => openDrill("kpi", { label: "Active SKUs" })} />
        <Agg testid="kpi-stockout-skus" origin={syn("SKU × branch stock positions")} label="Stockout SKUs" value="312" status="critical" onClick={() => openDrill("kpi", { label: "Stockout SKUs" })} />
        <Agg testid="kpi-overstock-skus" origin={syn("SKU × branch stock positions")} label="Overstock SKUs" value="487" status="warning" onClick={() => openDrill("kpi", { label: "Overstock SKUs" })} />
        <Agg testid="kpi-deadstock-value" origin={syn("idle stock ≥120 days")} label="Dead-Stock Value" value={formatPeso(DEAD_STOCK.reduce((s, d) => s + d.value, 0), { compact: true })} status="critical" onClick={() => openDrill("kpi", { label: "Dead-Stock Value" })} />
        <Agg testid="kpi-replen-queue" origin={syn("AI replenishment queue")} label="Auto-Replen Queue" value={REPLEN_QUEUE.length} status="info" onClick={() => openDrill("kpi", { label: "Auto-Replen Queue" })} />
        <Agg testid="kpi-dsi" origin={syn("days sales of inventory")} label="DSI" value="38.7d" status="success" onClick={() => openDrill("kpi", { label: "DSI" })} />
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* ABC × XYZ matrix — recomputed by the 8-step classification pipeline */}
        <AbcXyzMatrix className="col-span-12 lg:col-span-7" summary={summary} error={summaryError} onOpenCell={setCellDrill} />

        {/* DOH histogram */}
        <Tile className="col-span-12 lg:col-span-5" testid="doh-histogram" origin={syn("days-on-hand distribution")}
              title="Days-on-Hand Distribution"
              action={<div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted">
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-critical" />Stockout</span>
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-success" />Healthy</span>
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-warning" />Overstock</span>
              </div>}>
          <div style={{ height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={DOH_HISTOGRAM} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
                <CartesianGrid stroke={C.border} strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="range" tick={tickStyle} stroke={C.borderStrong} />
                <YAxis tick={tickStyle} stroke={C.borderStrong} />
                <Tooltip content={<TT formatter={v => `${v} SKUs`} />} cursor={{ fill: C.surface2 }} />
                <ReferenceArea x1="0-10" x2="10-20" fill={C.critical} fillOpacity={0.06} />
                <ReferenceArea x1="60-70" x2="120-130" fill={C.warning} fillOpacity={0.06} />
                <Bar dataKey="count" radius={[3, 3, 0, 0]} isAnimationActive={false}>
                  {DOH_HISTOGRAM.map((b, i) => (
                    <Cell key={i} fill={b.mid < 20 ? C.critical : b.mid > 60 ? C.warning : C.success} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="text-[11px] text-muted mt-1 leading-relaxed">
            <strong className="text-fg">Healthy band:</strong> 20–60 days. Left tail (red) = stockout risk. Right tail (amber) = overstock & working-capital drag.
          </div>
        </Tile>

        <PolicyRulesPanel className="col-span-12" rules={summary?.rules} run={summary?.run} testid="policy-rules" />

        {/* Stockout Heat Calendar */}
        <Tile className="col-span-12 lg:col-span-7" testid="stockout-calendar" origin={syn("90-day stockout calendar")}
              title="90-Day Stockout Heat Calendar — A-Class SKUs"
              action={<div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted">
                <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5" style={{ background: CAL_COLORS[0] }} />Healthy</span>
                <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5" style={{ background: CAL_COLORS[1] }} />Low</span>
                <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5" style={{ background: CAL_COLORS[2] }} />Stockout</span>
                <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5" style={{ background: CAL_COLORS[3] }} />Critical</span>
              </div>}>
          <div className="overflow-x-auto">
            <div className="min-w-[760px]">
              {STOCKOUT_CALENDAR.map(row => (
                <button key={row.skuId} data-testid={`stockout-row-${row.skuId}`}
                     onClick={() => openDrill("stockout-sku", row)}
                     className="w-full flex items-center gap-2 mb-1 hover:bg-surface-2/60 rounded-sm p-1 -mx-1 text-left transition-colors group">
                  <div className="w-44 shrink-0">
                    <div className="text-[11px] truncate font-medium flex items-center gap-1">
                      {row.name}
                      <ChevronRight size={10} className="opacity-0 group-hover:opacity-100 text-primary" />
                    </div>
                    <div className="text-[9px] text-muted font-mono-tabular">{row.skuId}</div>
                  </div>
                  <div className="flex gap-[2px] flex-1">
                    {row.days.map((d, i) => (
                      <div key={i} className="h-4 flex-1" style={{ background: CAL_COLORS[d], minWidth: 4 }}
                           title={`Day -${89 - i}: ${["healthy", "low", "stockout", "critical"][d]}`} />
                    ))}
                  </div>
                </button>
              ))}
              <div className="flex items-center gap-2 mt-1">
                <div className="w-44 shrink-0" />
                <div className="flex-1 flex justify-between text-[9px] text-subtle font-mono-tabular">
                  <span>D-90</span><span>D-60</span><span>D-30</span><span>Today</span>
                </div>
              </div>
            </div>
          </div>
        </Tile>

        {/* Turnover trend */}
        <Tile className="col-span-12 lg:col-span-5" testid="turnover-trend" origin={syn("turnover by category")} title="Inventory Turnover by Category">
          <div style={{ height: 240 }}>
            <ResponsiveContainer>
              <LineChart data={TURNOVER_TREND} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid stroke={C.border} strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="month" tick={tickStyle} stroke={C.borderStrong} />
                <YAxis tick={tickStyle} stroke={C.borderStrong} tickFormatter={v => `${v}×`} domain={[6, 14]} />
                <Tooltip content={<TT formatter={v => `${v}×`} />} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Line type="monotone" dataKey="Auto" stroke={C.chart[0]} strokeWidth={1.8} dot={false} isAnimationActive={false} />
                <Line type="monotone" dataKey="HVAC" stroke={C.chart[1]} strokeWidth={1.8} dot={false} isAnimationActive={false} />
                <Line type="monotone" dataKey="Refrig" stroke={C.chart[2]} strokeWidth={1.8} dot={false} isAnimationActive={false} />
                <Line type="monotone" dataKey="Consumables" stroke={C.chart[3]} strokeWidth={1.8} dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="text-[11px] text-muted mt-1">
            <strong className="text-fg">Target band:</strong> 8–12× per year. Consumables consistently above; Refrig under-rotates — review assortment.
          </div>
        </Tile>

        {/* AI Auto-Replenishment Queue */}
        <Tile className="col-span-12 lg:col-span-7" testid="replen-queue" origin={syn("AI replenishment queue")}
              title="AI Auto-Replenishment Queue"
              action={<Badge status="info">{replenRows.filter(r => !replenStatus[r.id]).length} pending</Badge>}>
          <div className="max-h-[420px] overflow-y-auto -mx-4">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-surface z-10">
                <tr className="text-[10px] uppercase tracking-widest text-muted">
                  <th className="py-2 px-3 text-left">SKU · Branch</th>
                  <th className="py-2 px-3 text-right">Current</th>
                  <th className="py-2 px-3 text-right">Suggest</th>
                  <th className="py-2 px-3 text-right">Value</th>
                  <th className="py-2 px-3 text-right">Conf.</th>
                  <th className="py-2 px-3 text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {replenRows.length === 0 && (
                  <tr><td colSpan={6} data-testid="replen-empty" className="py-6 text-center text-[12px] text-muted">No queued replenishment for this branch.</td></tr>
                )}
                {replenRows.map(r => {
                  const status = replenStatus[r.id];
                  return (
                    <tr key={r.id} data-testid={`replen-${r.id}`}
                        className={cn("border-t border-border", status === "approved" ? "bg-success/5" : status === "held" ? "bg-surface-2/50 opacity-60" : "")}>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-2">
                          <span className={cn("w-1.5 h-1.5 rounded-full",
                            r.urgency === "critical" ? "bg-critical animate-pulse" : r.urgency === "warning" ? "bg-warning" : "bg-info")} />
                          <button data-testid={`replen-drill-${r.id}`} onClick={() => openDrill("replen", r)}
                               className="min-w-0 text-left hover:text-primary transition-colors group">
                            <div className="text-sm truncate max-w-[280px] font-medium flex items-center gap-1">
                              {r.sku}
                              <ChevronRight size={10} className="opacity-0 group-hover:opacity-100 text-primary shrink-0" />
                            </div>
                            <div className="text-[10px] text-muted">{r.branch} · {r.region} · {r.reason}</div>
                          </button>
                        </div>
                      </td>
                      <td className="py-2 px-3 text-right font-mono-tabular">{r.currentUnits}</td>
                      <td className="py-2 px-3 text-right font-mono-tabular text-primary font-semibold">{r.suggestedQty}</td>
                      <td className="py-2 px-3 text-right font-mono-tabular">{formatPeso(r.value, { compact: true })}</td>
                      <td className="py-2 px-3 text-right font-mono-tabular">{(r.confidence * 100).toFixed(0)}%</td>
                      <td className="py-2 px-3 text-center">
                        {!status ? (
                          <div className="inline-flex gap-1">
                            <button data-testid={`replen-approve-${r.id}`}
                                    onClick={() => setReplenStatus(s => ({ ...s, [r.id]: "approved" }))}
                                    className="px-2 py-1 text-[10px] border border-success/40 text-success hover:bg-success/10 rounded-sm">Approve</button>
                            <button onClick={() => setReplenStatus(s => ({ ...s, [r.id]: "held" }))}
                                    className="px-2 py-1 text-[10px] border border-border text-muted hover:bg-surface-2 rounded-sm">Hold</button>
                          </div>
                        ) : (
                          <Badge status={status === "approved" ? "success" : "neutral"}>{status === "approved" ? "✓ Approved" : "On Hold"}</Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Tile>

        {/* Dead-Stock Detector */}
        <Tile className="col-span-12 lg:col-span-5" testid="dead-stock" origin={syn("dead-stock detector")}
              title="Dead-Stock Detector — ≥120 days idle"
              action={<Badge status="critical">{formatPeso(deadRows.reduce((s, d) => s + d.value, 0), { compact: true })} at risk</Badge>}>
          <div className="max-h-[420px] overflow-y-auto -mx-4">
            <div className="space-y-0 divide-y divide-border">
              {deadRows.length === 0 && <div data-testid="dead-stock-empty" className="px-4 py-6 text-center text-[12px] text-muted">No dead stock flagged for this branch.</div>}
              {deadRows.map((d, i) => (
                <button key={i} data-testid={`dead-stock-${i}`}
                     onClick={() => openDrill("deadstock", d)}
                     className="w-full text-left px-4 py-2.5 hover:bg-surface-2/70 transition-colors group">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate flex items-center gap-1">
                        {d.sku}
                        <ChevronRight size={11} className="opacity-0 group-hover:opacity-100 text-primary shrink-0" />
                      </div>
                      <div className="text-[10px] text-muted">{d.branch} · {d.region}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-mono-tabular text-sm font-semibold">{formatPeso(d.value, { compact: true })}</div>
                      <div className="text-[10px] text-critical font-mono-tabular">{d.daysIdle}d · {d.units}u</div>
                    </div>
                  </div>
                  <div className="mt-1.5 text-[11px] text-primary inline-flex items-center gap-1">
                    {d.recommendation.startsWith("Liquidate") ? <Trash2 size={11} /> : <ArrowRightLeft size={11} />}
                    {d.recommendation}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </Tile>

        {/* Original heatmap kept for SKU × Branch detail */}
        <Tile className="col-span-12" testid="inventory-heatmap" origin={syn("SKU × branch stock heatmap")}
              title="SKU × Branch Stockout / Overstock Heatmap"
              action={<div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-muted">
                <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5" style={{ background: HEAT_COLORS.stockout.bg }} />Stockout</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5" style={{ background: HEAT_COLORS.low.bg }} />Low</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5" style={{ background: HEAT_COLORS.healthy.bg }} />Healthy</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5" style={{ background: HEAT_COLORS.high.bg }} />High</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5" style={{ background: HEAT_COLORS.overstock.bg }} />Overstock</span>
              </div>}>
          <div className="overflow-x-auto">
            <table className="min-w-full text-[11px]">
              <thead>
                <tr>
                  <th className="text-left text-muted uppercase tracking-widest font-semibold py-2 pr-3 sticky left-0 bg-surface">SKU</th>
                  {branches.map(b => (
                    <th key={b.id} className="px-0.5 pb-2 text-muted font-mono-tabular" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", fontSize: 9, whiteSpace: "nowrap" }}>
                      {b.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {matrix.map(row => (
                  <tr key={row.sku.id}>
                    <td className="py-1 pr-3 text-fg sticky left-0 bg-surface" style={{ maxWidth: 200 }}>
                      <div className="text-[11px] font-medium truncate">{row.sku.name}</div>
                      <div className="text-[9px] text-muted">{row.sku.id} · {row.sku.abc}</div>
                    </td>
                    {row.cells.map((c, i) => {
                      const colors = HEAT_COLORS[c.state];
                      return (
                        <td key={i} className="p-0.5">
                          <div data-testid={`heat-${row.sku.id}-${c.branch.id}`}
                            onClick={() => setSelectedCell({ sku: row.sku, ...c })}
                            className="heat-cell w-7 h-7 cursor-pointer flex items-center justify-center font-mono-tabular text-[9px] font-semibold"
                            style={{ background: colors.bg, color: colors.txt }}
                            title={`${row.sku.name} @ ${c.branch.name}: ${c.value} days · ${c.state}`}>
                            {c.state === "stockout" ? "✕" : c.value}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {selectedCell && (
            <div className="mt-4 p-3 bg-surface-2 border border-border rounded-sm flex flex-wrap items-center gap-x-6 gap-y-2 text-xs">
              <div><span className="text-muted">SKU:</span> <strong>{selectedCell.sku.name}</strong></div>
              <div><span className="text-muted">Branch:</span> <strong>{selectedCell.branch.name}</strong></div>
              <div className="font-mono-tabular"><span className="text-muted">Days on Hand:</span> <strong>{selectedCell.value}</strong></div>
              <Badge status={selectedCell.state === "stockout" || selectedCell.state === "low" ? "critical" : selectedCell.state === "overstock" || selectedCell.state === "high" ? "warning" : "success"}>{selectedCell.state}</Badge>
              <button className="ml-auto text-primary hover:underline text-xs" onClick={() => setSelectedCell(null)}>Clear</button>
            </div>
          )}
        </Tile>
      </div>

      {cellDrill && <CellDrillDrawer cell={cellDrill} summary={summary} onClose={() => setCellDrill(null)} />}

      <InventoryDrillDrawer
        open={!!drill}
        drill={drillData}
        hotspotType={drill?.type}
        onClose={closeDrill}
      />
    </div>
  );
}

function Agg({ label, value, status, onClick, testid, origin }) {
  return (
    <div role={onClick ? "button" : undefined} tabIndex={onClick ? 0 : undefined}
      data-testid={testid}
      onClick={onClick}
      onKeyDown={onClick ? (e) => { if (e.key === "Enter") onClick(); } : undefined}
      className={cn("px-4 py-3 text-left w-full transition-colors group",
        onClick ? "cursor-pointer hover:bg-surface-2/60" : "")}>
      <div className="kpi-label flex items-center gap-2">
        {status && <StatusDot status={status} />}
        {label}
        {origin && <OriginBadge {...origin} className="ml-auto" testid={`${testid}-origin`} />}
      </div>
      <div className="kpi-value text-2xl mt-1.5">{value}</div>
    </div>
  );
}
